import { useCombobox } from "downshift";
import { useId, useMemo, useState } from "react";
import { filtrarOpciones, sinTildes, type OpcionBuscable } from "@/lib/buscar";
import { cn } from "@/lib/cn";
import { LabeledField } from "./LabeledField";

/** Opción de un `Select`: `busqueda` es texto extra donde también se busca y que no se muestra. */
export type OpcionSelect = OpcionBuscable;

export interface SelectProps {
  etiqueta: string;
  opciones: OpcionSelect[];
  valor: string | undefined;
  onCambio: (valor: string) => void;
  placeholder?: string;
  error?: string | null;
  deshabilitado?: boolean;
  className?: string;
  /** Si está, cuando lo escrito no coincide exacto (sin mayúsculas ni tildes) con ninguna opción, ofrece al final
   * "Agregar «texto»"; al elegirla (click o Enter, nunca al salir del campo) llama a `onCrear(texto recortado)`.
   * Quien lo usa crea el registro y después pasa el `valor` nuevo. */
  onCrear?: (texto: string) => void;
  /** Texto de la opción de crear (por defecto `Agregar «texto»`). */
  textoCrear?: (texto: string) => string;
}

const { InputChange, InputBlur } = useCombobox.stateChangeTypes;
const CREAR = "\u0000crear";
const textoCrearPorDefecto = (texto: string) => `Agregar «${texto}»`;

/** Desplegable que guarda un valor elegido. Siempre se puede escribir: la lista se filtra sin tildes y por palabras.
 * Tocar el campo muestra la lista completa; si se escribe y se sale sin elegir, vuelve a mostrar lo elegido.
 * Nunca usar <select> nativo ni un desplegable sin escritura: siempre este. */
export function Select({
  etiqueta, opciones, valor, onCambio, placeholder = "Escribí o tocá para ver la lista", error, deshabilitado, className, onCrear, textoCrear = textoCrearPorDefecto,
}: SelectProps) {
  const id = useId();
  const elegida = opciones.find((o) => o.valor === valor) ?? null;
  // null: no se está escribiendo (el campo muestra lo elegido y la lista va completa).
  const [texto, setTexto] = useState<string | null>(null);
  const visibles = useMemo(() => {
    if (texto === null) return opciones;
    const filtradas = filtrarOpciones(opciones, texto);
    const nuevo = texto.trim();
    if (!onCrear || !nuevo || opciones.some((o) => sinTildes(o.etiqueta.trim()) === sinTildes(nuevo))) return filtradas;
    return [...filtradas, { valor: CREAR, etiqueta: textoCrear(nuevo) }];
  }, [opciones, texto, onCrear, textoCrear]);

  const { isOpen, highlightedIndex, getInputProps, getMenuProps, getItemProps } = useCombobox({
    inputId: id,
    items: visibles,
    itemToString: (o) => o?.etiqueta ?? "",
    itemToKey: (o) => o?.valor,
    selectedItem: elegida,
    inputValue: texto ?? elegida?.etiqueta ?? "",
    // Al escribir queda resaltada la primera coincidencia: Enter (o salir del campo) la elige.
    // Salir del campo nunca crea: "Agregar «…»" sólo con click o Enter.
    stateReducer: (estado, { type, changes }) => {
      if (type === InputChange && changes.inputValue) return { ...changes, highlightedIndex: 0 };
      if (type === InputBlur && changes.selectedItem?.valor === CREAR) return { ...changes, selectedItem: estado.selectedItem };
      return changes;
    },
    onInputValueChange: ({ inputValue, type }) => {
      if (type === InputChange) setTexto(inputValue ?? "");
    },
    onSelectedItemChange: ({ selectedItem }) => {
      const nuevo = texto?.trim() ?? "";
      setTexto(null);
      if (selectedItem?.valor === CREAR) onCrear?.(nuevo);
      else if (selectedItem) onCambio(selectedItem.valor);
    },
    onIsOpenChange: ({ isOpen: abierto }) => {
      if (!abierto) setTexto(null);
    },
  });

  return (
    <LabeledField etiqueta={etiqueta} htmlFor={id} error={error} className={cn("relative", className)}>
      <div className="relative">
        <input
          {...getInputProps({ "aria-labelledby": undefined })}
          placeholder={placeholder}
          disabled={deshabilitado}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            "h-11 w-full rounded-control border border-borde-fuerte bg-superficie pl-3 pr-8",
            "placeholder:text-texto-suave focus:border-primario focus:outline-none focus:ring-2 focus:ring-primario/25 disabled:bg-superficie-2",
            error && "border-peligro",
          )}
        />
        <span aria-hidden className="pointer-events-none absolute inset-y-0 right-3 grid place-items-center text-texto-suave">
          ▾
        </span>
      </div>
      <ul
        {...getMenuProps({ "aria-labelledby": undefined, "aria-label": etiqueta })}
        className={cn(
          "absolute top-full z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-control border border-borde bg-superficie p-1 shadow-flotante",
          !isOpen && "hidden",
        )}
      >
        {isOpen &&
          visibles.map((o, i) => (
            <li
              key={o.valor}
              {...getItemProps({ item: o, index: i })}
              className={cn(
                "flex min-h-10 cursor-pointer select-none items-center rounded-md px-3 py-2",
                highlightedIndex === i && "bg-primario-suave",
                o.valor === valor && "font-medium",
              )}
            >
              {o.etiqueta}
            </li>
          ))}
        {isOpen && visibles.length === 0 && <li className="px-3 py-2 text-texto-suave">Ninguna coincide</li>}
      </ul>
    </LabeledField>
  );
}
